import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  Building2,
  CalendarRange,
  FileWarning,
  FileMinus2,
  Loader2,
  RotateCcw,
  Search,
  User as UsuarioIcono,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import type { Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type {
  Cliente,
  Emision,
  FacturaDelCliente,
  FacturaReemitida,
  MotivoDeCorreccion,
} from '@/shared/api/tipos';
import { DialogoDeCorreccionEnLote } from './DialogoDeCorreccionEnLote';
import { DialogoDeNotaDeCredito } from './DialogoDeNotaDeCredito';
import { CampoDelPanel, PanelLateral, claseEntradaPanel } from '@/shared/ui/PanelLateral';
import { notificar } from '@/shared/ui/avisos';

export function PanelDeCorreccion({ emisiones }: { emisiones: Emision[] }) {
  const clienteDeConsultas = useQueryClient();

  const [busqueda, setBusqueda] = useState('');
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [periodo, setPeriodo] = useState('');
  const [aAnular, setAAnular] = useState<FacturaDelCliente | null>(null);
  const [aAcreditar, setAAcreditar] = useState<FacturaDelCliente | null>(null);
  const [enLote, setEnLote] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientes = useQuery({
    queryKey: ['clientes', 'correccion', busqueda],
    queryFn: () =>
      api.get<Pagina<Cliente>>('/clientes', {
        query: { texto: busqueda, tamanio: 6, activo: true },
      }),
    enabled: busqueda.trim().length >= 3,
  });

  const facturas = useQuery({
    queryKey: ['facturas-del-cliente', cliente?.id, periodo],
    queryFn: () => api.get<FacturaDelCliente[]>(`/clientes/${cliente?.id}/facturas/${periodo}`),
    enabled: cliente !== null && periodo !== '',
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['facturas-del-cliente'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['emisiones-reporte'] });
  }

  const reemitir = useMutation({
    mutationFn: () =>
      api.post<FacturaReemitida>(`/emisiones/${periodo}/reemitir/${cliente?.id}`, {}),
    onSuccess: (f) => {
      refrescar();
      notificar.exito(
        `Factura de ${periodo} reemitida para ${cliente?.nombre}: $${f.total.toFixed(2)}`,
      );
    },
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo reemitir';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const emitidos = emisiones
    .filter((e) => e.estado === 'completada')
    .map((e) => e.periodo)
    .sort()
    .reverse();

  function limpiar() {
    setError(null);
    reemitir.reset();
  }

  return (
    <div className="space-y-5">
      <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <FileWarning className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-justify text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">Anular y volver a emitir son dos pasos.</span> Se anula
          porque la factura estaba mal —el predio equivocado, una exoneración sin registrar— y queda
          constancia de por qué, quién lo pidió y quién lo autorizó. La corregida se emite después,
          y el sistema exige que la anterior esté anulada: sin esa comprobación, reemitir sería una
          vía para cobrar dos veces el mismo mes.
        </p>
      </div>

      <div className="rounded-2xl border border-armazon-200 bg-white p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid content-start gap-2">
            <span className="text-sm font-semibold uppercase text-armazon-900">
              Cliente<span className="ml-0.5 text-alerta">*</span>
            </span>

            {cliente ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-marca-200 bg-marca-50 px-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold uppercase text-armazon-800">
                    {cliente.nombre}
                  </span>
                  <span className="importe block text-[11px] text-armazon-500">
                    {cliente.identificacion}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setCliente(null);
                    setBusqueda('');
                    limpiar();
                  }}
                  className="shrink-0 text-[11px] font-semibold uppercase text-marca-700 hover:underline"
                >
                  Cambiar
                </button>
              </div>
            ) : (
              <>
                <span className="relative">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
                    aria-hidden
                  />
                  <input
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Nombre, cédula, RUC, cuenta o medidor…"
                    className="h-11 w-full rounded-lg border border-armazon-200 bg-white pl-9 pr-3 text-sm outline-none transition-colors focus:border-marca-400"
                  />
                </span>

                {busqueda.trim().length >= 3 && (
                  <ul className="max-h-48 overflow-y-auto rounded-lg border border-armazon-200">
                    {clientes.isLoading && (
                      <li className="px-3 py-2 text-xs uppercase text-armazon-400">Buscando…</li>
                    )}
                    {clientes.data?.contenido.length === 0 && (
                      <li className="px-3 py-2 text-xs uppercase text-armazon-400">
                        Ningún cliente coincide
                      </li>
                    )}
                    {(clientes.data?.contenido ?? []).map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => setCliente(c)}
                          className="flex w-full items-center gap-2.5 border-b border-armazon-100 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-armazon-50"
                        >
                          <span
                            className={`grid size-7 shrink-0 place-items-center rounded-lg ${
                              c.identificacion.length === 13
                                ? 'bg-amber-50 text-amber-600'
                                : 'bg-indigo-50 text-indigo-500'
                            }`}
                          >
                            {c.identificacion.length === 13 ? (
                              <Building2 className="size-3.5" aria-hidden />
                            ) : (
                              <UsuarioIcono className="size-3.5" aria-hidden />
                            )}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-xs font-medium uppercase text-armazon-800">
                              {c.nombre}
                            </span>
                            <span className="importe block text-[10px] text-armazon-500">
                              {c.identificacion}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </label>

          <label className="grid content-start gap-2">
            <span className="text-sm font-semibold uppercase text-armazon-900">
              Periodo<span className="ml-0.5 text-alerta">*</span>
            </span>
            <select
              value={periodo}
              onChange={(e) => {
                setPeriodo(e.target.value);
                limpiar();
              }}
              className="h-11 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm uppercase text-armazon-900 outline-none transition-colors focus:border-marca-400"
            >
              <option value="">SELECCIONAR</option>
              {emitidos.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <span className="text-xs uppercase text-armazon-500">
              Solo los meses que ya se emitieron
            </span>
            {cliente && (
              <button
                type="button"
                onClick={() => setEnLote(true)}
                className="mt-1 inline-flex items-center gap-2 self-start text-[11px] font-semibold uppercase text-marca-700 underline-offset-2 hover:underline"
              >
                <CalendarRange className="size-3.5" aria-hidden />
                El error viene de varios meses
              </button>
            )}
          </label>
        </div>

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}
      </div>

      {cliente && periodo && (
        <div className="space-y-3">
          {facturas.isLoading && (
            <Cargando titulo="Buscando sus facturas" detalle="Del periodo elegido" alto="h-28" />
          )}

          {facturas.data?.length === 0 && (
            <p className="rounded-2xl border border-armazon-200 bg-white p-10 text-center text-sm uppercase text-armazon-400">
              Este cliente no tiene factura de {periodo}
            </p>
          )}

          {(facturas.data ?? []).map((f) => (
            <TarjetaDeFactura
              key={f.id}
              factura={f}
              alAnular={() => setAAnular(f)}
              alAcreditar={() => setAAcreditar(f)}
              alReemitir={() => reemitir.mutate()}
              reemitiendo={reemitir.isPending}
            />
          ))}
        </div>
      )}

      <DialogoDeAnulacion
        key={aAnular?.id ?? 'ninguna'}
        factura={aAnular}
        alCerrar={() => setAAnular(null)}
        alAnular={refrescar}
      />

      <DialogoDeCorreccionEnLote
        abierto={enLote}
        clienteId={cliente?.id ?? null}
        clienteNombre={cliente?.nombre ?? ''}
        meses={emitidos}
        alCerrar={() => setEnLote(false)}
        alTerminar={refrescar}
      />

      <DialogoDeNotaDeCredito
        abierto={aAcreditar !== null}
        factura={
          aAcreditar === null
            ? null
            : {
                id: aAcreditar.id,
                numero: aAcreditar.numero,
                cliente: cliente?.nombre ?? '',
                total: aAcreditar.total,
              }
        }
        alCerrar={() => setAAcreditar(null)}
        alEmitir={refrescar}
      />
    </div>
  );
}

function TarjetaDeFactura({
  factura: f,
  alAnular,
  alAcreditar,
  alReemitir,
  reemitiendo,
}: {
  factura: FacturaDelCliente;
  alAnular: () => void;
  alAcreditar: () => void;
  alReemitir: () => void;
  reemitiendo: boolean;
}) {
  const anulada = f.estado === 'anulada';

  return (
    <div
      className={`rounded-2xl border bg-white p-5 ${
        anulada ? 'border-armazon-200' : 'border-marca-200'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="importe text-sm font-bold text-armazon-800">
              FACTURA {String(f.id).padStart(6, '0')}
            </span>
            <Insignia texto={f.estado} tono={anulada ? 'gris' : 'verde'} />
            {f.situacionSri !== 'interna' && <Insignia texto={f.situacionSri} tono="azul" />}
          </p>
          <p className="mt-1 text-[11px] uppercase text-armazon-500">
            {f.periodo} · emitida {formatearCorto(f.fechaEmision)} · vence{' '}
            {formatearCorto(f.vencimiento)} · {f.cuantosPredios}{' '}
            {f.cuantosPredios === 1 ? 'predio' : 'predios'}
          </p>
        </div>

        <span
          className={`importe text-xl font-bold ${
            anulada ? 'text-armazon-300 line-through' : 'text-armazon-800'
          }`}
        >
          ${f.total.toFixed(2)}
        </span>
      </div>

      {f.motivoCodigo && (
        <div className="mt-4 rounded-lg border border-armazon-200 bg-armazon-50/60 p-3">
          <p className="text-[11px] font-semibold uppercase text-armazon-700">
            {f.motivoCodigo} · {f.motivoNombre}
          </p>
          {f.observacion && (
            <p className="mt-1 text-justify text-[11px] leading-relaxed text-armazon-600">
              {f.observacion}
            </p>
          )}
          {f.facturaNuevaId != null && (
            <p className="mt-1.5 text-[10px] uppercase text-marca-700">
              Corregida con la factura {String(f.facturaNuevaId).padStart(6, '0')}
            </p>
          )}
        </div>
      )}

      {anulada && f.correccionId == null && (
        <p className="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-amber-800">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          Esta factura está anulada pero no consta por qué. No se reemite en automático: hay que
          revisarla.
        </p>
      )}

      {f.esperandoReemision && (
        <p className="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-amber-800">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold">Falta emitir la corregida.</span> Se recalcula con la
            tarifa y la exoneración de hoy, no con las de entonces: si alguna cambió desde que se
            anuló, la nueva no coincidirá con la anterior.
          </span>
        </p>
      )}

      {(f.sePuedeAnular || f.sePuedeAcreditar || f.esperandoReemision) && (
        <div className="mt-4 flex justify-end gap-2">
          {f.sePuedeAnular && (
            <button
              type="button"
              onClick={alAnular}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold uppercase text-red-700 transition-colors hover:bg-red-50"
            >
              <Ban className="size-4" aria-hidden />
              Anular
            </button>
          )}

          {f.sePuedeAcreditar && (
            <button
              type="button"
              onClick={alAcreditar}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-300 px-4 py-2 text-sm font-semibold uppercase text-amber-700 transition-colors hover:bg-amber-50"
            >
              <FileMinus2 className="size-4" aria-hidden />
              Acreditar
            </button>
          )}
          {f.esperandoReemision && (
            <button
              type="button"
              onClick={alReemitir}
              disabled={reemitiendo}
              className="inline-flex items-center gap-2 rounded-lg bg-marca-600 px-4 py-2 text-sm font-semibold uppercase text-white transition-colors hover:bg-marca-700 disabled:opacity-40"
            >
              {reemitiendo ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <RotateCcw className="size-4" aria-hidden />
              )}
              Emitir la corregida
            </button>
          )}
        </div>
      )}

      {!f.sePuedeAnular && !f.sePuedeAcreditar && !f.esperandoReemision && !anulada && (
        <p className="mt-4 flex gap-2 rounded-lg border border-armazon-200 bg-armazon-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {f.estado === 'pagada'
            ? 'Esta factura está pagada. Anularla dejaría un cobro sin obligación: primero hay que resolver el pago.'
            : f.situacionSri === 'acreditada'
              ? 'Esta factura ya tiene una nota de crédito. Si el SRI la autorizó, queda anulada y el mes se puede volver a emitir.'
              : 'El SRI todavía no la ha resuelto. Hasta que conteste no se puede ni anular ni acreditar: pregunte por ella.'}
        </p>
      )}
    </div>
  );
}

function DialogoDeAnulacion({
  factura,
  alCerrar,
  alAnular,
}: {
  factura: FacturaDelCliente | null;
  alCerrar: () => void;
  alAnular: () => void;
}) {
  const [motivoId, setMotivoId] = useState('');
  const [observacion, setObservacion] = useState('');
  const [aprobadoPor, setAprobadoPor] = useState('');
  const [respaldo, setRespaldo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const motivos = useQuery({
    queryKey: ['motivos-de-correccion', 'vigentes'],
    queryFn: () => api.get<MotivoDeCorreccion[]>('/motivos-de-correccion'),
    enabled: factura !== null,
  });

  const elegido = (motivos.data ?? []).find((m) => String(m.id) === motivoId) ?? null;

  const listo =
    elegido !== null &&
    (!elegido.exigeObservacion || observacion.trim() !== '') &&
    (!elegido.requiereAprobacion || aprobadoPor.trim() !== '');

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo || factura === null) {
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      await api.post(`/facturas/${factura.id}/anular`, {
        motivoId: Number(motivoId),
        observacion: observacion.trim() || undefined,
        aprobadoPor: aprobadoPor.trim() || undefined,
        respaldo: respaldo.trim() || undefined,
      });

      alAnular();
      alCerrar();
      notificar.exito(`Factura ${String(factura.id).padStart(6, '0')} anulada`);
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo anular la factura';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PanelLateral
      abierto={factura !== null}
      alCerrar={alCerrar}
      titulo="Anular la factura"
      descripcion={
        factura === null
          ? ''
          : `${String(factura.id).padStart(6, '0')} · ${factura.periodo} · $${factura.total.toFixed(2)}`
      }
      icono={Ban}
      alEnviar={enviar}
      enviando={enviando || !listo}
    >
      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-amber-800">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <span>
          La deuda de este mes deja de existir a nombre del cliente. La factura no se borra: se
          conserva anulada, con esta constancia, y después habrá que emitir la corregida.
        </span>
      </p>

      <CampoDelPanel etiqueta="Motivo" requerido ayuda="Sale del catálogo, no se escribe.">
        <select
          value={motivoId}
          onChange={(evento) => setMotivoId(evento.target.value)}
          className={claseEntradaPanel}
        >
          <option value="">SELECCIONAR</option>
          {(motivos.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.codigo} · {m.nombre}
            </option>
          ))}
        </select>
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Qué pasó"
        requerido={elegido?.exigeObservacion ?? false}
        ayuda="El detalle del caso: cuál era el dato y cuál es el correcto."
      >
        <textarea
          value={observacion}
          onChange={(evento) => setObservacion(evento.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="El predio figuraba en la parroquia urbana y está en Aláquez…"
          className={`${claseEntradaPanel} h-auto py-2`}
        />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Quién lo autorizó"
        requerido={elegido?.requiereAprobacion ?? false}
        ayuda="El responsable que dio el visto bueno."
      >
        <input
          value={aprobadoPor}
          onChange={(evento) => setAprobadoPor(evento.target.value)}
          maxLength={120}
          className={claseEntradaPanel}
        />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Respaldo"
        ayuda="Memorando, oficio o trámite que sustenta la corrección."
      >
        <input
          value={respaldo}
          onChange={(evento) => setRespaldo(evento.target.value)}
          maxLength={255}
          placeholder="Memorando GADCL-EPAGAL-…"
          className={claseEntradaPanel}
        />
      </CampoDelPanel>
    </PanelLateral>
  );
}

function Insignia({ texto, tono }: { texto: string; tono: 'verde' | 'gris' | 'azul' }) {
  const tonos = {
    verde: 'bg-marca-50 text-marca-700',
    gris: 'bg-armazon-100 text-armazon-500',
    azul: 'bg-blue-50 text-blue-700',
  };
  return (
    <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase ${tonos[tono]}`}>
      {texto.replace(/_/g, ' ')}
    </span>
  );
}

function formatearCorto(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
